import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaArrowRight,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaShareAlt,
  FaTimes,
} from "react-icons/fa";
import CommunityDirectoryPage from "./CommunityDirectoryPage";
import CommunityShowcase from "./CommunityShowcase";
import { resolveCommunityMeta } from "./communityDirectoryConfig";
import { getAllBusinessesByCommunity } from "../services/businessDirectoryService";
import ComingSoonDirectory from "./ComingSoonDirectory";
import "flag-icons/css/flag-icons.min.css";
import "./CommunityShowcase.css";

const CATEGORY_ICONS = {
  restaurants: "🍴",
  restaurant: "🍴",
  groceries: "🛒",
  grocery: "🛒",
  foodtruck: "🚚",
  "food truck": "🚚",
  catering: "🍲",
  beauty: "💄",
  realtors: "🏘️",
  realtor: "🏘️",
  "home help": "🏡",
  photography: "📷",
  gardening: "🌱",
  "child care": "👶",
};

function normalizeCategory(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function isRestaurant(category) {
  return ["restaurant", "restaurants"].includes(category);
}

function isGrocery(category) {
  return ["grocery", "groceries"].includes(category);
}

function isFoodTruck(category) {
  return ["foodtruck", "food truck", "food trucks"].includes(category);
}

function isPremiumBusiness(business) {
  return (
    String(business?.listing_plan || "").toLowerCase() === "premium" ||
    business?.legacy_premium_label === true
  );
}

function toDirectoryItem(business) {
  const category = normalizeCategory(business.category);
  const profilePath = String(business.profile_path || "").trim();
  const website = String(business.website || "").trim();

  return {
    id: business.id,
    name: business.name,
    icon: CATEGORY_ICONS[category] || "🏪",
    specialties: Array.isArray(business.specialties)
      ? business.specialties
      : [],
    address: business.address || "",
    phone: business.phoneNumber || "",
    url: profilePath || website,
    mapUrl: business.map_url || "",
    external: !profilePath && Boolean(website),
    category,
  };
}

function buildDirectoryData(businesses) {
  const result = {
    premiumListings: [],
    restaurants: [],
    groceries: [],
    foodTrucks: [],
    otherBusinesses: [],
  };

  businesses.forEach((business) => {
    const item = toDirectoryItem(business);

    if (isPremiumBusiness(business)) {
      result.premiumListings.push(item);
      return;
    }

    if (isRestaurant(item.category)) {
      result.restaurants.push(item);
      return;
    }

    if (isGrocery(item.category)) {
      result.groceries.push(item);
      return;
    }

    if (isFoodTruck(item.category)) {
      result.foodTrucks.push(item);
      return;
    }

    result.otherBusinesses.push(item);
  });

  return result;
}

const EMPTY_DATA = {
  premiumListings: [],
  restaurants: [],
  groceries: [],
  foodTrucks: [],
  otherBusinesses: [],
};

/* =========================================================
   Showcase variant the new "inside a community" design.
   Rendered only when variant="showcase" is passed, so every
   community still using the classic layout is untouched.
   Row tap: business profile page (profile_path) → website →
   built-in details sheet, in that order.
   ========================================================= */
function ShowcaseCommunityView({ community, businesses, claimText }) {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);
  const meta = resolveCommunityMeta(community) || {};

  /* Windows Chrome can't render flag EMOJI (shows letters like
     "ET"), so use the same flag-icons image library the sidebar
     uses. Falls back to meta.icon / globe when no code exists. */
  const heroIcon = meta.flag ? (
    <span
      className={`fi fi-${String(meta.flag).toLowerCase()} ci-hero__flagimg`}
      aria-hidden="true"
    />
  ) : (
    meta.icon || "🌍"
  );

  useEffect(() => {
    if (!selected) return undefined;
    const onKey = (event) => event.key === "Escape" && setSelected(null);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [selected]);

  const openBusiness = (business) => {
    const profilePath = String(business?.profile_path || "").trim();
    const website = String(business?.website || "").trim();

    if (profilePath) {
      navigate(profilePath);
      return;
    }

    if (website) {
      const href = /^https?:\/\//i.test(website) ? website : `https://${website}`;
      window.open(href, "_blank", "noopener");
      return;
    }

    setSelected(business);
  };

  const openMap = (business) => {
    const direct = String(business?.map_url || "").trim();
    if (direct) {
      window.open(direct, "_blank", "noopener");
      return;
    }
    const query =
      String(business?.address || "").trim() || `${business?.name || ""} San Antonio`;
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`,
      "_blank",
      "noopener"
    );
  };

  const shareBusiness = async (business) => {
    const profilePath = String(business?.profile_path || "").trim();
    const url = profilePath
      ? `${window.location.origin}${profilePath}`
      : window.location.href;
    const text = `${business?.name || "A business"} on GoOurHub`;

    try {
      if (navigator.share) {
        await navigator.share({ title: text, text, url });
        return;
      }
    } catch {
      return; // user dismissed the sheet
    }

    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      window.alert("Link copied.");
    } catch {
      window.alert(url);
    }
  };

  const phone = String(selected?.phoneNumber || "").trim();
  const address = String(selected?.address || "").trim();

  return (
    <div className="ci-page">
      <CommunityShowcase
        title={meta.label || community}
        icon={heroIcon}
        businesses={businesses}
        loading={false}
        error=""
        communitySlug={community}
        onSelectBusiness={openBusiness}
      />

      {claimText && (
        <div className="ci-claim">
          <p>{claimText}</p>
          <Link to="/partner-signup#joinForm" className="ci-claim__btn">
            List your business <FaArrowRight aria-hidden="true" />
          </Link>
        </div>
      )}

      {selected && (
        <div className="ci-sheetwrap" onClick={() => setSelected(null)}>
          <div
            className="ci-sheet"
            role="dialog"
            aria-modal="true"
            aria-label={selected?.name || "Business details"}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="ci-sheet__x"
              onClick={() => setSelected(null)}
              aria-label="Close"
            >
              <FaTimes />
            </button>

            <h3 className="ci-sheet__nm">{selected?.name}</h3>

            <p className="ci-sheet__tg">
              {[selected?.category, ...(Array.isArray(selected?.specialties) ? selected.specialties.slice(0, 2) : [])]
                .filter(Boolean)
                .join(" · ")}
            </p>

            {address && <p className="ci-sheet__ad">{address}</p>}

            <div className="ci-sheet__acts">
              {phone && (
                <a
                  className="ci-sheet__act"
                  href={`tel:${phone.replace(/[^\d+]/g, "")}`}
                >
                  <FaPhoneAlt /> Call
                </a>
              )}
              <button type="button" className="ci-sheet__act" onClick={() => openMap(selected)}>
                <FaMapMarkerAlt /> Directions
              </button>
              <button type="button" className="ci-sheet__act" onClick={() => shareBusiness(selected)}>
                <FaShareAlt /> Share
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ApiCommunityDirectoryPage({
  community,
  eyebrow,
  title,
  description,
  tags,
  claimText,
  modalIdPrefix,
  sectionDescriptions,
  /* Default is now the showcase design for ALL communities.
     Any page can opt back with variant="classic". */
  variant = "showcase",
}) {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    async function loadBusinesses() {
      setLoading(true);
      setError("");

      try {
        const records = await getAllBusinessesByCommunity(community, {
          signal: controller.signal,
        });
        setBusinesses(records);
      } catch (requestError) {
        if (requestError.name !== "AbortError") {
          setError(
            requestError.message ||
              "The business directory could not be loaded. Please try again."
          );
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }

    loadBusinesses();

    return () => controller.abort();
  }, [community, reloadKey]);

  const data = useMemo(
    () => (loading || error ? EMPTY_DATA : buildDirectoryData(businesses)),
    [businesses, error, loading]
  );

  if (loading) {
    return (
      <div className="visit-resturants-page">
        <div className="categories-container">
          <div className="visit-empty" role="status">
            Loading {community} businesses…
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="visit-resturants-page">
        <div className="categories-container">
          <div className="visit-empty" role="alert">
            <p>{error}</p>
            <button
              type="button"
              className="visit-card__btn"
              onClick={() => setReloadKey((current) => current + 1)}
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (variant === "showcase") {
    return (
      <ShowcaseCommunityView
        community={community}
        businesses={businesses}
        claimText={claimText}
      />
    );
  }

  return (
    <CommunityDirectoryPage
      data={data}
      eyebrow={eyebrow}
      title={title}
      description={description}
      tags={tags}
      claimText={claimText}
      modalIdPrefix={modalIdPrefix}
      sectionDescriptions={sectionDescriptions}
    />
  );
}
